import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateIT } from "@/lib/date";
import { TrendingUp, RefreshCw, Flag } from "lucide-react";

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function currentMonthYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function StatsPage() {
  const [month, setMonth] = useState(currentMonthYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (m) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/stats/overview?month=${m}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento statistiche");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(month); }, [month]);

  const [y, m] = month.split("-").map(Number);

  const setY = (newY) => setMonth(`${newY}-${String(m).padStart(2, "0")}`);
  const setM = (newM) => setMonth(`${y}-${String(newM).padStart(2, "0")}`);

  const years = [];
  const nowY = new Date().getFullYear();
  for (let yr = 2024; yr <= nowY + 1; yr++) years.push(yr);

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div className="card-surface p-5 flex flex-wrap items-end gap-4">
        <div>
          <div className="label-caps mb-2">Mese</div>
          <select data-testid="stats-month-select" value={m} onChange={(e) => setM(parseInt(e.target.value))}
            className="bg-transparent border border-white/15 px-3 py-2 text-sm">
            {MONTH_NAMES.map((n, i) => <option key={i + 1} value={i + 1} className="bg-[#120B19]">{n}</option>)}
          </select>
        </div>
        <div>
          <div className="label-caps mb-2">Anno</div>
          <select data-testid="stats-year-select" value={y} onChange={(e) => setY(parseInt(e.target.value))}
            className="bg-transparent border border-white/15 px-3 py-2 text-sm">
            {years.map(yr => <option key={yr} value={yr} className="bg-[#120B19]">{yr}</option>)}
          </select>
        </div>
        <button data-testid="stats-reload-btn" onClick={() => load(month)} className="btn-ghost text-xs">
          <RefreshCw size={12} className="inline mr-1" /> Ricalcola
        </button>
        {loading && <span className="text-xs text-white/40">Caricamento…</span>}
        {error && <span className="text-xs text-[#FF3366]" data-testid="stats-error">{error}</span>}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          testid="kpi-nuovi"
          icon={<TrendingUp size={16} />}
          label="Nuovi iscritti"
          value={data?.nuovi_iscritti?.count ?? "—"}
          accent="#10B981"
        />
        <StatCard
          testid="kpi-rinnovi"
          icon={<RefreshCw size={16} />}
          label="Rinnovi pacchetto"
          value={data?.rinnovi_pacchetto?.count ?? "—"}
          accent="#A855F7"
        />
        <StatCard
          testid="kpi-conclusi"
          icon={<Flag size={16} />}
          label="Percorsi conclusi"
          value={data?.percorsi_conclusi?.count ?? "—"}
          accent="#F59E0B"
        />
      </div>

      <ListCard
        testid="list-nuovi"
        title={`Nuovi iscritti · ${MONTH_NAMES[m - 1]} ${y}`}
        rows={data?.nuovi_iscritti?.clients || []}
        empty="Nessun nuovo iscritto nel mese selezionato."
        render={(c) => (
          <div className="flex justify-between items-center gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{c.email}</div>
            </div>
            <div className="text-xs text-white/50 shrink-0">{formatDateIT(c.created_at?.slice(0, 10))}</div>
          </div>
        )}
      />

      <ListCard
        testid="list-rinnovi"
        title={`Rinnovi pacchetto · ${MONTH_NAMES[m - 1]} ${y}`}
        rows={data?.rinnovi_pacchetto?.clients || []}
        empty="Nessun rinnovo pacchetto nel mese selezionato."
        render={(c) => (
          <div className="flex justify-between items-center gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{c.email}</div>
            </div>
            <div className="text-xs shrink-0 flex items-center gap-3">
              <span className="text-[#A855F7]">+{c.lessons_added}</span>
              <span className="text-white/50">{formatDateIT(c.recharge_at?.slice(0, 10))}</span>
            </div>
          </div>
        )}
      />

      <ListCard
        testid="list-conclusi"
        title={`Percorsi conclusi · ${MONTH_NAMES[m - 1]} ${y}`}
        rows={data?.percorsi_conclusi?.clients || []}
        empty="Nessun percorso concluso nel mese selezionato."
        render={(c) => (
          <div className="flex justify-between items-center gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{c.email}</div>
            </div>
            <div className="text-xs text-white/50 shrink-0">{formatDateIT(c.completed_at?.slice(0, 10))}</div>
          </div>
        )}
      />
    </div>
  );
}

function StatCard({ testid, icon, label, value, accent }) {
  return (
    <div className="card-surface p-6" data-testid={testid}>
      <div className="label-caps flex items-center gap-2" style={{ color: accent }}>{icon} {label}</div>
      <div className="font-heading font-black text-5xl mt-3" data-testid={`${testid}-value`}>{value}</div>
    </div>
  );
}

function ListCard({ testid, title, rows, empty, render }) {
  return (
    <div className="card-surface p-6" data-testid={testid}>
      <div className="label-caps mb-3">{title}</div>
      {rows.length === 0 && <div className="text-white/40 text-sm" data-testid={`${testid}-empty`}>{empty}</div>}
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="border border-white/5 p-3" data-testid={`${testid}-row-${c.id}`}>
            {render(c)}
          </div>
        ))}
      </div>
    </div>
  );
}
